import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, Database } from 'lucide-react'
import { WidgetTab as KBWidgetTab } from '@/components/knowledge/WidgetTab'
import type { ChatAssistant } from '@/components/knowledge/WidgetTab/types'
import { AssistantDetail } from '@/hooks/useAssistants'
import { useQuery } from '@tanstack/react-query'
import knowledgeApi from '@/lib/knowledge-api'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'

interface WidgetTabProps {
  assistantId: string
  assistant: AssistantDetail
  tenantSlug?: string // Optional - will use KB's tenant_slug if available
}

/**
 * Widget tab for Chat Assistants.
 * Uses the first connected KB's widget configuration.
 * The widget is tied to the KB's chat assistant, not the assistant entity itself.
 */
export function WidgetTab({ assistantId: _assistantId, assistant, tenantSlug: propTenantSlug }: WidgetTabProps) {
  // Get the first connected KB to use its widget settings
  const firstKB = assistant.knowledge_bases?.[0]
  
  // Fetch KB detail to get chat_assistant data
  const { data: kbDetail, isLoading } = useQuery({
    queryKey: ['knowledge-base', firstKB?.id],
    queryFn: async () => {
      const response = await knowledgeApi.get(`/knowledge/api/kb/${firstKB?.id}/`)
      return response.data
    },
    enabled: !!firstKB?.id,
  })
  
  // No KBs connected
  if (!firstKB) {
    return (
      <Alert>
        <Database className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between">
          <span>
            No knowledge bases connected. Connect a knowledge base to enable the widget.
          </span>
          <Button variant="outline" size="sm" asChild>
            <Link to={`/app/assistants/${_assistantId}`}>
              Go to Knowledge Bases tab
            </Link>
          </Button>
        </AlertDescription>
      </Alert>
    )
  }
  
  // Loading KB details
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading widget configuration...</div>
      </div>
    )
  }
  
  // KB doesn't have a chat assistant
  if (!kbDetail?.chat_assistant) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Widget not available. The connected knowledge base "{firstKB.name}" doesn't have a chat assistant configured.
          Please ensure the knowledge base has parsed documents.
        </AlertDescription>
      </Alert>
    )
  }
  
  // Use the KB's chat assistant data for widget settings
  // The shared_id must be the KB's chat_assistant.shared_id for saving settings to work correctly
  const chatAssistant: ChatAssistant = {
    id: kbDetail.chat_assistant.id, // Use KB's chat assistant ID
    name: assistant.name, // Use assistant name for display
    ragflow_chat_id: kbDetail.chat_assistant.ragflow_chat_id || '',
    shared_id: kbDetail.chat_assistant.shared_id || '', // MUST use KB's chat_assistant.shared_id for save to work
    authentication_token: kbDetail.chat_assistant.authentication_token || '',
    
    // Widget appearance from KB's chat assistant
    header_color: kbDetail.chat_assistant.header_color || '',
    chat_title: kbDetail.chat_assistant.chat_title || assistant.name,
    chat_title_color: kbDetail.chat_assistant.chat_title_color || '',
    assistant_chat_bubble_color: kbDetail.chat_assistant.assistant_chat_bubble_color || '',
    assistant_chat_font_color: kbDetail.chat_assistant.assistant_chat_font_color || '',
    profile_chat_bubble_color: kbDetail.chat_assistant.profile_chat_bubble_color || '',
    profile_chat_font_color: kbDetail.chat_assistant.profile_chat_font_color || '',
    widget_button_color: kbDetail.chat_assistant.widget_button_color || '',
    
    // Avatar settings
    show_avatar: kbDetail.chat_assistant.show_avatar ?? true,
    assistant_avatar_url: kbDetail.chat_assistant.assistant_avatar_url || '',
    user_avatar_url: kbDetail.chat_assistant.user_avatar_url || '',
    profile_icon_url: kbDetail.chat_assistant.profile_icon_url || '',
    
    // Behavior
    agent: kbDetail.chat_assistant.agent ?? false,
    
    // Widget settings
    widget_settings: kbDetail.chat_assistant.widget_settings,
  }
  
  // Use tenant_slug from KB API response, fallback to prop
  const tenantSlug = kbDetail?.tenant_slug || propTenantSlug || ''
  
  return (
    <KBWidgetTab
      kbId={firstKB.id}
      chatAssistant={chatAssistant}
      tenantSlug={tenantSlug}
      isLoading={false}
    />
  )
}
